import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Radio,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'

import { CommunityProfile } from '../../../../models/Company/types'
import {
    emptyCommunityProfile,
    useCommunityProfile,
} from '../../../../hooks/useCommunityProfile'
import {
    FillDecisions,
    FillReview,
    FillStepId,
    ListPair,
    ScalarPair,
    SourceChoice,
    buildFillReview,
    fillReviewSteps,
    formatFaq,
    formatFee,
    formatHoursBlock,
    formatLastFilledAt,
    initialFillDecisions,
    resolveFilledProfile,
    reviewHasWork,
    stepConflictsResolved,
} from './fillFromWebsiteReview'

const useStyles = makeStyles((theme: Theme) => ({
    paper: {
        maxWidth: 760,
        width: '100%',
    },
    titleRow: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
    },
    caption: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        lineHeight: 1.5,
    },
    body: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        minHeight: 280,
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
    },
    pairGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.spacing(1.5),
        [theme.breakpoints.down('xs')]: {
            gridTemplateColumns: '1fr',
        },
    },
    choiceCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        padding: theme.spacing(1.25),
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
    },
    choiceCardSelected: {
        borderColor: theme.palette.primary.main,
        boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
    },
    choiceLabel: {
        fontWeight: 600,
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    choiceValue: {
        fontSize: '0.875rem',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        lineHeight: 1.45,
    },
    fieldBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        paddingBottom: theme.spacing(1.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    fieldTitle: {
        fontWeight: 600,
        fontSize: '0.9rem',
    },
    logoPreview: {
        height: 56,
        maxWidth: 180,
        objectFit: 'contain',
        backgroundColor: theme.palette.grey[50],
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 6,
        padding: theme.spacing(0.5),
    },
    reading: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(6, 2),
        textAlign: 'center',
    },
    stepHint: {
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
    },
}))

const STEP_TITLES: Record<FillStepId, string> = {
    summary: 'Review what we found',
    logo: 'Property logo',
    hours: 'Office hours',
    facts: 'Property facts',
    more: 'More details',
    faqs: 'Frequently asked questions',
}

type Phase = 'start' | 'reading' | 'review' | 'empty' | 'failed'

type Props = {
    open: boolean
    apartmentId: number
    currentProfile?: CommunityProfile
    initialUrl?: string
    onClose: () => void
    onApplied?: (profile: CommunityProfile) => void
}

export const FillFromWebsiteDialog: React.FC<Props> = ({
    open,
    apartmentId,
    currentProfile,
    initialUrl,
    onClose,
    onApplied,
}) => {
    const classes = useStyles()
    const {
        startExtract,
        isExtracting,
        extractJob,
        applyExtract,
        isApplyingExtract,
    } = useCommunityProfile(apartmentId)
    const [url, setUrl] = useState(initialUrl || '')
    const [phase, setPhase] = useState<Phase>('start')
    const [stepIndex, setStepIndex] = useState(0)
    const [decisions, setDecisions] = useState<FillDecisions>(
        initialFillDecisions({
            logo: null,
            hours: null,
            facts: [],
            more: [],
            fees: [],
            faqs: [],
        }),
    )
    const [startedJobId, setStartedJobId] = useState<number | null>(null)

    const current = currentProfile || emptyCommunityProfile()
    const proposal = extractJob?.result || {}
    const review = useMemo(
        () =>
            buildFillReview(
                current,
                proposal,
                extractJob?.logo_url || proposal.logo_url,
            ),
        [current, proposal, extractJob?.logo_url],
    )
    const steps = useMemo(() => fillReviewSteps(review), [review])
    const activeStep = steps[stepIndex] || 'summary'

    useEffect(() => {
        if (open) {
            setUrl(initialUrl || '')
            setPhase('start')
            setStepIndex(0)
            setStartedJobId(null)
        }
    }, [open, initialUrl])

    useEffect(() => {
        if (!open || !extractJob || extractJob.id !== startedJobId) return
        if (extractJob.status === 'queued' || extractJob.status === 'running') {
            setPhase('reading')
            return
        }
        if (extractJob.status === 'failed') {
            setPhase('failed')
            return
        }
        if (
            extractJob.status === 'ready' &&
            (phase === 'reading' || phase === 'start')
        ) {
            const nextReview = buildFillReview(
                current,
                extractJob.result || {},
                extractJob.logo_url,
            )
            setDecisions(initialFillDecisions(nextReview))
            setStepIndex(0)
            setPhase(reviewHasWork(nextReview) ? 'review' : 'empty')
        }
    }, [open, extractJob, startedJobId, phase])

    const handleStart = async () => {
        try {
            const job = await startExtract(url.trim() || undefined)
            if (job?.id) {
                setStartedJobId(job.id)
                setPhase('reading')
            }
        } catch {
            // toast handled by hook
        }
    }

    const handleApply = async () => {
        if (!extractJob?.id) return
        const resolved = resolveFilledProfile(
            current,
            proposal,
            review,
            decisions,
        )
        try {
            const saved = await applyExtract({
                jobId: extractJob.id,
                profile: {
                    ...resolved.profile,
                    website_url: extractJob.start_url || url,
                },
                applyLogo: resolved.applyLogo,
                logoUrl: resolved.logoUrl,
            })
            if (saved) {
                onApplied?.(saved)
                onClose()
            }
        } catch {
            // toast handled by hook
        }
    }

    const canAdvance =
        phase !== 'review' ||
        stepConflictsResolved(activeStep, review, decisions)

    const renderStart = () => (
        <Box className={classes.body}>
            <Typography className={classes.caption}>
                We&apos;ll read this URL and extract property details. If a
                field already has saved data and the website says something
                different, you&apos;ll choose which version to keep. Nothing is
                saved until you apply.
            </Typography>
            <TextField
                label="Website URL"
                placeholder="https://www.property.com"
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                variant="outlined"
                size="small"
                fullWidth
            />
            {current.website_filled_at && (
                <Typography className={classes.caption}>
                    Last applied {formatLastFilledAt(current.website_filled_at)}
                    {current.website_fill_url
                        ? ` · ${current.website_fill_url}`
                        : ''}
                    .
                </Typography>
            )}
            <Box className={classes.actions}>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleStart}
                    disabled={!url.trim() || isExtracting}
                >
                    Start
                </Button>
            </Box>
        </Box>
    )

    const renderReading = () => (
        <Box className={classes.reading}>
            <CircularProgress />
            <Typography>Reading the property website…</Typography>
            <Typography className={classes.caption}>
                {extractJob?.pages?.length
                    ? `Read ${extractJob.pages.length} page${
                          extractJob.pages.length === 1 ? '' : 's'
                      }.`
                    : 'This usually takes a minute.'}
            </Typography>
            <Button onClick={onClose}>Cancel</Button>
        </Box>
    )

    const renderFailed = () => (
        <Box className={classes.body}>
            <Typography className={classes.caption}>
                {extractJob?.error || 'Could not read the website.'}
            </Typography>
            <Box className={classes.actions}>
                <Button onClick={onClose}>Close</Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleStart}
                >
                    Try again
                </Button>
            </Box>
        </Box>
    )

    const renderEmpty = () => (
        <Box className={classes.body}>
            <Typography className={classes.caption}>
                We didn&apos;t find new property details on this site.
            </Typography>
            <Box className={classes.actions}>
                <Button onClick={onClose}>Close</Button>
            </Box>
        </Box>
    )

    const setScalar = (key: ScalarPair['key'], choice: SourceChoice) => {
        setDecisions((prev) => ({
            ...prev,
            scalars: { ...prev.scalars, [key]: choice },
        }))
    }

    const renderChoicePair = (
        currentLabel: string,
        websiteLabel: string,
        currentValue: string,
        websiteValue: string,
        selected: SourceChoice | undefined,
        onSelect: (choice: SourceChoice) => void,
        currentMedia?: React.ReactNode,
        websiteMedia?: React.ReactNode,
    ) => (
        <Box className={classes.pairGrid}>
            <Box
                className={`${classes.choiceCard} ${
                    selected === 'current' ? classes.choiceCardSelected : ''
                }`}
                onClick={() => onSelect('current')}
            >
                <Box display="flex" alignItems="center">
                    <Radio
                        color="primary"
                        checked={selected === 'current'}
                        onChange={() => onSelect('current')}
                    />
                    <Typography className={classes.choiceLabel}>
                        {currentLabel}
                    </Typography>
                </Box>
                {currentMedia}
                <Typography className={classes.choiceValue}>
                    {currentValue || 'Not set'}
                </Typography>
            </Box>
            <Box
                className={`${classes.choiceCard} ${
                    selected === 'website' ? classes.choiceCardSelected : ''
                }`}
                onClick={() => onSelect('website')}
            >
                <Box display="flex" alignItems="center">
                    <Radio
                        color="primary"
                        checked={selected === 'website'}
                        onChange={() => onSelect('website')}
                    />
                    <Typography className={classes.choiceLabel}>
                        {websiteLabel}
                    </Typography>
                </Box>
                {websiteMedia}
                <Typography className={classes.choiceValue}>
                    {websiteValue || 'Not set'}
                </Typography>
            </Box>
        </Box>
    )

    const renderAdditions = (items: { label: string; value: string }[]) => {
        if (!items.length) return null
        return (
            <Box>
                <Typography className={classes.fieldTitle}>
                    We&apos;ll add these
                </Typography>
                {items.map((item) => (
                    <Box key={item.label} mt={1}>
                        <Typography className={classes.choiceLabel}>
                            {item.label}
                        </Typography>
                        <Typography className={classes.choiceValue}>
                            {item.value}
                        </Typography>
                    </Box>
                ))}
            </Box>
        )
    }

    const renderListPairs = <T,>(
        items: ListPair<T>[],
        formatItem: (item: T) => string,
        group: 'fees' | 'faqs',
        conflictLabel: string,
    ) => (
        <>
            {items
                .filter((item) => item.type === 'conflict')
                .map((item) => (
                    <Box key={item.key} className={classes.fieldBlock}>
                        <Typography className={classes.fieldTitle}>
                            {conflictLabel}
                        </Typography>
                        {renderChoicePair(
                            'Current',
                            'From website',
                            item.current ? formatItem(item.current) : '',
                            item.website ? formatItem(item.website) : '',
                            decisions[group][item.key]?.choice,
                            (choice) =>
                                setDecisions((prev) => ({
                                    ...prev,
                                    [group]: {
                                        ...prev[group],
                                        [item.key]: {
                                            ...prev[group][item.key],
                                            include: true,
                                            choice,
                                        },
                                    },
                                })),
                        )}
                    </Box>
                ))}
            {items
                .filter((item) => item.type === 'addition')
                .map((item) => (
                    <FormControlLabel
                        key={item.key}
                        control={
                            <Checkbox
                                color="primary"
                                checked={
                                    decisions[group][item.key]?.include !==
                                    false
                                }
                                onChange={(event) =>
                                    setDecisions((prev) => ({
                                        ...prev,
                                        [group]: {
                                            ...prev[group],
                                            [item.key]: {
                                                ...prev[group][item.key],
                                                include: event.target.checked,
                                            },
                                        },
                                    }))
                                }
                            />
                        }
                        label={`Add: ${
                            item.website ? formatItem(item.website) : ''
                        }`}
                    />
                ))}
            {items
                .filter((item) => item.type === 'keep')
                .map((item) => (
                    <FormControlLabel
                        key={item.key}
                        control={
                            <Checkbox
                                color="primary"
                                checked={
                                    decisions[group][item.key]?.include !==
                                    false
                                }
                                onChange={(event) =>
                                    setDecisions((prev) => ({
                                        ...prev,
                                        [group]: {
                                            ...prev[group],
                                            [item.key]: {
                                                ...prev[group][item.key],
                                                include: event.target.checked,
                                            },
                                        },
                                    }))
                                }
                            />
                        }
                        label={`Keep: ${
                            item.current ? formatItem(item.current) : ''
                        }`}
                    />
                ))}
        </>
    )

    const renderScalarSection = (pairs: ScalarPair[]) => (
        <>
            {pairs
                .filter((item) => item.type === 'conflict')
                .map((item) => (
                    <Box key={item.key} className={classes.fieldBlock}>
                        <Typography className={classes.fieldTitle}>
                            {item.label}
                        </Typography>
                        {renderChoicePair(
                            'Current',
                            'From website',
                            item.current,
                            item.website,
                            decisions.scalars[item.key],
                            (choice) => setScalar(item.key, choice),
                        )}
                    </Box>
                ))}
            {renderAdditions(
                pairs
                    .filter((item) => item.type === 'addition')
                    .map((item) => ({
                        label: item.label,
                        value: item.website,
                    })),
            )}
        </>
    )

    const additionSummary = (nextReview: FillReview) => {
        const rows: { label: string; value: string }[] = []
        if (nextReview.logo) {
            rows.push({ label: 'Logo', value: 'Use the logo from the website' })
        }
        if (nextReview.hours) {
            rows.push({
                label: 'Office hours',
                value: formatHoursBlock(nextReview.hours.website),
            })
        }
        nextReview.facts.concat(nextReview.more).forEach((item) => {
            rows.push({ label: item.label, value: item.website })
        })
        nextReview.fees
            .filter((item) => item.type === 'addition' && item.website)
            .forEach((item) =>
                rows.push({
                    label: 'Fee',
                    value: formatFee(item.website!),
                }),
            )
        nextReview.faqs
            .filter((item) => item.type === 'addition' && item.website)
            .forEach((item) =>
                rows.push({
                    label: 'FAQ',
                    value: formatFaq(item.website!),
                }),
            )
        return rows
    }

    const renderStep = (step: FillStepId, nextReview: FillReview) => {
        if (step === 'summary') {
            return renderAdditions(additionSummary(nextReview))
        }
        if (step === 'logo' && nextReview.logo) {
            if (nextReview.logo.type === 'addition') {
                return (
                    <Box>
                        <Typography className={classes.caption}>
                            We&apos;ll use this logo from the website.
                        </Typography>
                        <Box mt={1}>
                            <img
                                src={nextReview.logo.websiteUrl}
                                alt="Website logo"
                                className={classes.logoPreview}
                            />
                        </Box>
                    </Box>
                )
            }
            return renderChoicePair(
                'Current logo',
                'Website logo',
                '',
                '',
                decisions.logo,
                (choice) => setDecisions((prev) => ({ ...prev, logo: choice })),
                nextReview.logo.currentUrl ? (
                    <img
                        src={nextReview.logo.currentUrl}
                        alt="Current logo"
                        className={classes.logoPreview}
                    />
                ) : null,
                <img
                    src={nextReview.logo.websiteUrl}
                    alt="Website logo"
                    className={classes.logoPreview}
                />,
            )
        }
        if (step === 'hours' && nextReview.hours) {
            if (nextReview.hours.type === 'addition') {
                return renderAdditions([
                    {
                        label: 'Office hours',
                        value: formatHoursBlock(nextReview.hours.website),
                    },
                ])
            }
            return renderChoicePair(
                'Current hours',
                'Website hours',
                formatHoursBlock(nextReview.hours.current),
                formatHoursBlock(nextReview.hours.website),
                decisions.hours,
                (choice) =>
                    setDecisions((prev) => ({ ...prev, hours: choice })),
            )
        }
        if (step === 'facts') return renderScalarSection(nextReview.facts)
        if (step === 'more') {
            return (
                <>
                    {renderScalarSection(nextReview.more)}
                    {renderListPairs(
                        nextReview.fees,
                        formatFee,
                        'fees',
                        'Monthly fee',
                    )}
                </>
            )
        }
        if (step === 'faqs') {
            return renderListPairs(
                nextReview.faqs,
                formatFaq,
                'faqs',
                'Question',
            )
        }
        return null
    }

    const renderReview = () => (
        <Box className={classes.body}>
            <Typography className={classes.stepHint}>
                Step {stepIndex + 1} of {steps.length}
            </Typography>
            <Typography className={classes.caption}>
                {activeStep === 'summary'
                    ? 'No saved fields conflict with the website. Apply to save what we found.'
                    : 'Choose which version to keep when both exist. New website values are added unless you turn them off.'}
            </Typography>
            {renderStep(activeStep, review)}
            <Box className={classes.actions}>
                <Button onClick={onClose}>Cancel</Button>
                {stepIndex > 0 && (
                    <Button onClick={() => setStepIndex((index) => index - 1)}>
                        Back
                    </Button>
                )}
                {stepIndex < steps.length - 1 ? (
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={() => setStepIndex((index) => index + 1)}
                        disabled={!canAdvance}
                    >
                        Next
                    </Button>
                ) : (
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleApply}
                        disabled={!canAdvance || isApplyingExtract}
                    >
                        {isApplyingExtract ? 'Saving…' : 'Apply'}
                    </Button>
                )}
            </Box>
        </Box>
    )

    const title =
        phase === 'start'
            ? 'Fill from website'
            : phase === 'reading'
            ? 'Reading website'
            : phase === 'failed'
            ? 'Could not read website'
            : phase === 'empty'
            ? 'Nothing new found'
            : STEP_TITLES[activeStep]

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            classes={{ paper: classes.paper }}
        >
            <DialogTitle disableTypography>
                <Box className={classes.titleRow}>
                    <Typography variant="h6">{title}</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                {phase === 'start' && renderStart()}
                {phase === 'reading' && renderReading()}
                {phase === 'failed' && renderFailed()}
                {phase === 'empty' && renderEmpty()}
                {phase === 'review' && renderReview()}
            </DialogContent>
        </Dialog>
    )
}
